package com.evoapps.evoshift.ui.finance

import com.evoapps.evoshift.util.usesDecimalComma
import com.evoapps.evoshift.util.appLanguage
import kotlin.math.abs
import kotlin.math.roundToLong

/*
 * The one place money and percentages become text (SH-67). The domain keeps raw doubles; every
 * screen, chart label and report formats them here: money with two decimals and the workplace
 * currency, shares with at most one decimal.
 */

/** "3 605,85 zł" for PLN, "3,605.85 EUR" otherwise: grouped thousands, two decimals. */
fun formatMoney(amount: Double, currency: String): String =
    if (currency == "PLN") "${formatDecimal(amount, 2, groupSeparator = '\u00A0', decimalSeparator = ',')} zł"
    else "${formatDecimal(amount, 2)} $currency"

/**
 * SH-191: money in whole units, "312 zł" for PLN, "312 EUR" otherwise: for SPMH (sales per
 * man-hour), whose cents say nothing. [formatMoneyPerHour] adds the "/h".
 */
fun formatWholeMoney(amount: Double, currency: String): String =
    if (currency == "PLN") "${formatDecimal(amount, 0, groupSeparator = '\u00A0')} zł"
    else "${formatDecimal(amount, 0)} $currency"

/**
 * A percentage with at most one decimal: "47.9%", "48%" (no ".0"); Polish uses a decimal comma
 * ("47,9%"). [value] is already in percent (47.9, not 0.479).
 */
fun formatPercent(value: Double, language: String = appLanguage()): String {
    val polish = usesDecimalComma(language)
    val text = formatDecimal(value, 1, groupSeparator = if (polish) '\u00A0' else ',', decimalSeparator = if (polish) ',' else '.')
    val trimmed = if (text.endsWith(if (polish) ",0" else ".0")) text.dropLast(2) else text
    return "$trimmed%"
}

/** [value] rounded half up to [digits] decimals, with grouped thousands. */
fun formatDecimal(value: Double, digits: Int, groupSeparator: Char = ',', decimalSeparator: Char = '.'): String {
    var factor = 1L
    repeat(digits) { factor *= 10 }
    val scaled = (abs(value) * factor).roundToLong()
    val whole = (scaled / factor).toString().reversed().chunked(3).joinToString(groupSeparator.toString()).reversed()
    val sign = if (value < 0 && scaled != 0L) "-" else ""
    if (digits == 0) return sign + whole
    val fraction = (scaled % factor).toString().padStart(digits, '0')
    return "$sign$whole$decimalSeparator$fraction"
}
